import { Vec3 } from '../../engine/math/vec3';
import { Quat } from '../../engine/math/quat';
import { DEG } from '../../engine/math/easing';
import { MeshData, Color, translate, rotateX, rotateZ, rotateY, setEmissive, scale as scaleMesh } from '../../engine/geo/mesh';
import { box, cylinder, cone, sphere, torusSegment, combine } from '../../engine/geo/primitives';

export type ItemId = 'sword' | 'lantern' | 'plank' | 'magnet' | 'goldKey' | 'whiteKey' | 'blackKey' | 'chalice' | 'greyDot';

export interface ItemDef {
  id: ItemId;
  name: string;
  metal: boolean;
  /** Physics sphere radius and pickup reach. */
  radius: number;
  mesh: () => MeshData;
  /** Origin height above ground when resting. */
  groundY: number;
  groundRot: Quat;
  handPos: Vec3;
  handRot: Quat;
  /** Emissive pulse period in seconds (0 = none). */
  pulse: number;
  /** Inline SVG silhouette for the HUD. */
  icon: string;
  heavy?: boolean;
}

const q = (yaw: number, pitch: number, roll = 0) => new Quat().setEuler(yaw * DEG, pitch * DEG, roll * DEG);

function keyMesh(color: Color, groove: Color, glow: number): MeshData {
  // shaft along +Z, bow (ring) at -Z end, teeth hanging -Y near the tip
  const shaft = translate(box(0.07, 0.07, 0.42, color), 0, 0, 0.1);
  const bow = rotateX(torusSegment(0.13, 0.04, Math.PI * 2, 12, 6, color, false), 0);
  translate(bow, 0, 0, -0.16);
  const t1 = translate(box(0.06, 0.1, 0.06, color), 0, -0.08, 0.26);
  const t2 = translate(box(0.06, 0.14, 0.06, color), 0, -0.1, 0.16);
  const grooves = setEmissive(translate(box(0.02, 0.02, 0.3, groove), 0.04, 0, 0.1), groove, glow);
  return combine(shaft, bow, t1, t2, grooves);
}

const KEY_ICON = (c: string) => `<svg viewBox="0 0 48 48"><circle cx="14" cy="24" r="8" fill="none" stroke="${c}" stroke-width="5"/><rect x="21" y="21.5" width="22" height="5" fill="${c}"/><rect x="34" y="26" width="4" height="7" fill="${c}"/><rect x="40" y="26" width="3" height="5" fill="${c}"/></svg>`;

export const ITEM_DEFS: Record<ItemId, ItemDef> = {
  sword: {
    id: 'sword', name: 'Sword', metal: true, radius: 0.35, pulse: 0,
    mesh: () => {
      const blade = box(0.09, 0.95, 0.025, [0.88, 0.9, 0.95]);
      translate(blade, 0, 0.62, 0);
      setEmissive(blade, [0.5, 0.55, 0.65], 0.12);
      const tip = translate(cone(0.045, 0.12, 4, [0.92, 0.94, 0.98]), 0, 1.15, 0);
      const guard = translate(box(0.34, 0.06, 0.07, [0.75, 0.6, 0.25]), 0, 0.13, 0);
      const grip = translate(cylinder(0.035, 0.04, 0.26, 7, [0.35, 0.2, 0.12]), 0, -0.02, 0);
      const pommel = translate(sphere(0.05, 7, 5, [0.75, 0.6, 0.25]), 0, -0.17, 0);
      return combine(blade, tip, guard, grip, pommel);
    },
    groundY: 0.06, groundRot: q(20, 0, 90), handPos: new Vec3(0, -0.02, 0.05), handRot: q(0, -100, 0),
    icon: `<svg viewBox="0 0 48 48"><path d="M24 4 L28 10 L28 30 L20 30 L20 10 Z" fill="#e8ecf2"/><rect x="14" y="30" width="20" height="4" fill="#d9a441"/><rect x="21.5" y="34" width="5" height="9" fill="#7a4a2b"/></svg>`,
  },
  lantern: {
    id: 'lantern', name: 'Lantern', metal: false, radius: 0.32, pulse: 0,
    mesh: () => {
      const dark: Color = [0.2, 0.18, 0.16];
      const base = translate(cylinder(0.14, 0.15, 0.06, 8, dark), 0, -0.2, 0);
      const top = translate(cone(0.15, 0.12, 8, dark), 0, 0.2, 0);
      const bars: MeshData[] = [];
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        bars.push(translate(box(0.02, 0.36, 0.02, dark), Math.cos(a) * 0.12, 0, Math.sin(a) * 0.12));
      }
      const glass = setEmissive(sphere(0.1, 8, 6, [1, 0.85, 0.5]), [1, 0.8, 0.45], 1.6);
      const ring = translate(rotateX(torusSegment(0.07, 0.015, Math.PI * 2, 10, 5, dark, false), 0), 0, 0.33, 0);
      return combine(base, top, ...bars, glass, ring);
    },
    groundY: 0.23, groundRot: q(0, 0, 0), handPos: new Vec3(0, -0.4, 0), handRot: q(0, 0, 0),
    icon: `<svg viewBox="0 0 48 48"><rect x="16" y="12" width="16" height="22" rx="2" fill="none" stroke="#c9b58f" stroke-width="3"/><circle cx="24" cy="23" r="6" fill="#ffd36b"/><path d="M20 12 L24 6 L28 12" fill="none" stroke="#c9b58f" stroke-width="3"/></svg>`,
  },
  plank: {
    id: 'plank', name: 'Plank', metal: false, radius: 0.5, pulse: 0, heavy: true,
    mesh: () => {
      const wood = box(4, 0.15, 0.4, [0.55, 0.38, 0.22]);
      const capA = translate(box(0.2, 0.17, 0.42, [0.38, 0.25, 0.14]), -1.95, 0, 0);
      const capB = translate(box(0.2, 0.17, 0.42, [0.38, 0.25, 0.14]), 1.95, 0, 0);
      return combine(wood, capA, capB);
    },
    groundY: 0.075, groundRot: q(0, 0, 0), handPos: new Vec3(0, -0.15, -0.2), handRot: q(90, 0, 0),
    icon: `<svg viewBox="0 0 48 48"><rect x="4" y="20" width="40" height="8" fill="#9c6b3c"/><rect x="4" y="20" width="4" height="8" fill="#5c3b1f"/><rect x="40" y="20" width="4" height="8" fill="#5c3b1f"/></svg>`,
  },
  magnet: {
    id: 'magnet', name: 'Magnet', metal: false, radius: 0.35, pulse: 0, heavy: true,
    mesh: () => {
      const u = torusSegment(0.22, 0.07, Math.PI, 12, 7, [0.85, 0.15, 0.12], false);
      rotateZ(u, 0); // arc from +X to -X over +Y
      const legL = translate(cylinder(0.07, 0.07, 0.2, 7, [0.85, 0.15, 0.12]), 0.22, -0.1, 0);
      const legR = translate(cylinder(0.07, 0.07, 0.2, 7, [0.85, 0.15, 0.12]), -0.22, -0.1, 0);
      const tipL = translate(cylinder(0.072, 0.072, 0.1, 7, [0.9, 0.9, 0.9]), 0.22, -0.25, 0);
      const tipR = translate(cylinder(0.072, 0.072, 0.1, 7, [0.9, 0.9, 0.9]), -0.22, -0.25, 0);
      return combine(u, legL, legR, tipL, tipR);
    },
    groundY: 0.31, groundRot: q(0, 0, 0), handPos: new Vec3(0, -0.35, 0), handRot: q(90, 0, 0),
    icon: `<svg viewBox="0 0 48 48"><path d="M12 30 V20 a12 12 0 0 1 24 0 V30" fill="none" stroke="#d63c2f" stroke-width="8"/><rect x="8" y="30" width="8" height="8" fill="#eee"/><rect x="32" y="30" width="8" height="8" fill="#eee"/></svg>`,
  },
  goldKey: {
    id: 'goldKey', name: 'Gold Key', metal: true, radius: 0.3, pulse: 2.2,
    mesh: () => keyMesh([0.95, 0.75, 0.2], [1, 0.9, 0.4], 0.5),
    groundY: 0.06, groundRot: q(30, 0, 90), handPos: new Vec3(0, -0.05, 0.1), handRot: q(0, -90, 0),
    icon: KEY_ICON('#f2c94c'),
  },
  whiteKey: {
    id: 'whiteKey', name: 'White Key', metal: true, radius: 0.3, pulse: 2.6,
    mesh: () => keyMesh([0.95, 0.93, 0.85], [1, 1, 0.9], 0.5),
    groundY: 0.06, groundRot: q(-40, 0, 90), handPos: new Vec3(0, -0.05, 0.1), handRot: q(0, -90, 0),
    icon: KEY_ICON('#f4ead6'),
  },
  blackKey: {
    id: 'blackKey', name: 'Black Key', metal: true, radius: 0.3, pulse: 1.8,
    mesh: () => keyMesh([0.1, 0.09, 0.1], [1, 0.15, 0.1], 1.2),
    groundY: 0.06, groundRot: q(10, 0, 90), handPos: new Vec3(0, -0.05, 0.1), handRot: q(0, -90, 0),
    icon: KEY_ICON('#3a2f36'),
  },
  chalice: {
    id: 'chalice', name: 'Chalice', metal: false, radius: 0.35, pulse: 1.6,
    mesh: () => {
      const gold: Color = [1, 0.8, 0.3];
      const foot = translate(cylinder(0.1, 0.18, 0.05, 10, gold), 0, -0.3, 0);
      const stem = translate(cylinder(0.035, 0.05, 0.3, 8, gold), 0, -0.13, 0);
      const bowl = translate(cylinder(0.24, 0.06, 0.28, 12, gold), 0, 0.14, 0);
      const inner = setEmissive(translate(cylinder(0.2, 0.05, 0.2, 12, [1, 0.95, 0.7]), 0, 0.2, 0), [1, 0.9, 0.5], 1.5);
      const m = combine(foot, stem, bowl, inner);
      setEmissive(m, [1, 0.75, 0.3], 0.35);
      return m;
    },
    groundY: 0.33, groundRot: q(0, 0, 0), handPos: new Vec3(0, -0.08, 0.05), handRot: q(0, 55, 10),
    icon: `<svg viewBox="0 0 48 48"><path d="M10 8 H38 L34 22 a10 10 0 0 1 -20 0 Z" fill="#f2c94c"/><rect x="22" y="28" width="4" height="10" fill="#f2c94c"/><rect x="14" y="38" width="20" height="4" rx="2" fill="#f2c94c"/></svg>`,
  },
  greyDot: {
    id: 'greyDot', name: 'Grey Dot', metal: true, radius: 0.2, pulse: 0,
    mesh: () => sphere(0.03, 6, 4, [0.5, 0.5, 0.5]),
    groundY: 0.03, groundRot: q(0, 0, 0), handPos: new Vec3(0, -0.05, 0), handRot: q(0, 0, 0),
    icon: `<svg viewBox="0 0 48 48"><circle cx="24" cy="24" r="4" fill="#888"/></svg>`,
  },
};

void rotateY; void scaleMesh;
